import { JSX } from 'react';
import { Chip, Tooltip } from '@mui/material';
import { useRecordContext } from 'react-admin';

/**
 * A Mystery Shopper or Compliance Sniffer result's status, coloured by whether it needs anyone.
 *
 * One component for both lists, because they are copies of each other and the pair has already
 * drifted once. It exists at all because of 'failed': a result whose scoring gave up looks
 * exactly like every other row in a plain text column, and the whole point of moving it out of
 * 'scoring' was to make it visible.
 */

type StatusLook = {
    label: string,
    color: 'default' | 'error' | 'warning' | 'info' | 'success',
    /** Why the row is in this state, for people who do not know the vocabulary. */
    hint: string,
}

const looks: Record<string, StatusLook> = {
    failed: {
        label: 'Failed',
        color: 'error',
        hint: 'Scoring stopped and has not been picked up again. Rescore it from Scoring Failures.',
    },
    out_of_time: {
        label: 'Out of time',
        color: 'warning',
        hint: 'The run closed without enough of a thread to score.',
    },
    scoring: { label: 'Scoring', color: 'info', hint: 'With an Operator now.' },
    running: { label: 'Running', color: 'info', hint: 'Collecting the email thread.' },
    active: { label: 'Active', color: 'info', hint: 'Sent and waiting for a reply.' },
    pending: { label: 'Pending', color: 'default', hint: 'Not started yet.' },
    cancelled: { label: 'Cancelled', color: 'default', hint: 'Stopped by hand.' },
    complete: { label: 'Complete', color: 'success', hint: 'Scored.' },
};

type MiniResultStatusFieldProps = {
    /**
     * The column heading.
     *
     * Declared even though nothing in this component reads it: React Admin's <Datagrid> takes the
     * header from the child element's own `label` prop, so without it here the header is blank
     * and TypeScript rejects passing one.
     */
    label?: string,
}

export const MiniResultStatusField: (props: MiniResultStatusFieldProps) => JSX.Element | null = (
    _props: MiniResultStatusFieldProps
): JSX.Element | null => {
    const record = useRecordContext<{ status?: string }>();
    const status: string = record?.status ?? '';
    if (status === '') {
        return null;
    }

    // An unknown status is shown as itself rather than swallowed. A backend that starts setting
    // something new should be visible here, not blank.
    const look: StatusLook = looks[status] ?? { label: status, color: 'default', hint: '' };

    return (
        <Tooltip title={ look.hint }>
            <Chip size="small" variant="outlined" color={ look.color } label={ look.label } />
        </Tooltip>
    );
}
